<template>
  <div class="caption-s">
    <essay-status-badge
      :id="badgeId"
      :status="item.status"
      :popover="badgesPopover"
      :is-grading="isGrading"
      :platform="effectivePlatform"
      :exported-at="item.exportedAt"
      @badge-click="handleBadgeClick"
    />

    <!-- Popover for Failed status -->
    <eg-b-popover
      v-if="item.status === 'Failed'"
      ref="popover"
      :show="show"
      :target="badgeId"
      triggers="focus hover"
      placement="top"
      @show="show = true"
      @hide="show = false"
    >
      <div class="popover-content-wrapper">
        <eg-button
          aria-label="Close"
          type="ordinary"
          icon="close"
          size="small"
          icon-size="small"
          icon-color="var(--color-icon-neutral-neutral)"
          class="popover-close-btn"
          @click="show = false"
        />
        <div class="popover-text">
          {{ popperText }}
        </div>

        <eg-button
          v-if="showPopoverButton"
          type="primary"
          icon="stars"
          class="w-100 mt-2"
          @click="onClickPopoverButton"
        >
          {{ popoverButtonText }}
        </eg-button>
      </div>
    </eg-b-popover>
  </div>
</template>

<script>
import { unpause } from "@/api";
import { errorTable } from "@/constants";
import EssayStatusBadge from "@/components/essay/status/essay_status_badge.vue";
import trackAndRedirectToUpgrade from "@/mixins/trackAndRedirectToUpgrade";

export default {
  components: {
    EssayStatusBadge,
  },

  mixins: [trackAndRedirectToUpgrade],

  inject: ["workspacesProvider"],

  props: {
    item: {
      type: Object,
      required: true,
    },
    isGrading: {
      type: Boolean,
      default: false,
    },
    badgesPopover: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      show: false,
    };
  },

  computed: {
    badgeId() {
      return `status-badge-${this.item.id}`;
    },

    effectivePlatform() {
      const folder = this.workspacesProvider.getFolderById(
        this.$route.params.workspaceKey,
        this.$route.params.id
      );
      return this.item.platform || folder?.platform || null;
    },

    showPopoverButton() {
      return (
        this.item.failureCode === "pausedLimitsExceeded" ||
        this.item.failureCode === "planWordLimitsExceeded" ||
        this.item.failureCode === "planEssayLimitsExceeded"
      );
    },

    popoverButtonText() {
      switch (this.item.failureCode) {
        case "pausedLimitsExceeded":
          return "Resume";
        case "planWordLimitsExceeded":
        case "planEssayLimitsExceeded":
          return "Upgrade your plan";
        default:
          return "";
      }
    },

    popperText() {
      switch (this.item.failureCode) {
        case "pausedLimitsExceeded":
        case "planEssayLimitsExceeded":
        case "missedPermissions:":
        case "essayTextMissed":
          return errorTable[this.item.failureCode];
        case "planWordLimitsExceeded":
          return "This essay exceeds the word count limit for your plan.";
        case "serviceError:":
          return this.item.failureReason || "";
        default:
          return "There was an unexpected error processing this essay. Please try again.";
      }
    },
  },

  methods: {
    handleBadgeClick() {
      if (this.item.status === "Failed") {
        this.show = !this.show;
      }
    },

    async onClickPopoverButton() {
      switch (this.item.failureCode) {
        case "pausedLimitsExceeded":
          await unpause(this);
          this.show = false;
          return;
        case "planWordLimitsExceeded":
        case "planEssayLimitsExceeded":
          this.show = false; // prevent modal overlapping
          return this.trackAndRedirectToUpgrade(this.item.failureCode);
        default:
          return;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.popover-content-wrapper {
  position: relative;
  padding-right: 20px; // Space for close button
}

.popover-close-btn {
  position: absolute;
  top: -4px;
  right: -8px;
  z-index: 1;
  background: white;
  border-radius: 50%;
  padding: 2px !important;
  width: 20px !important;
  height: 20px !important;
  min-width: 20px !important;
}

.popover-text {
  margin: 0;
}

::v-deep {
  .popover-body {
    padding: 8px 12px;
  }

  .popover-header {
    display: none; // Hide header completely since we moved close button
  }
}
</style>
